import * as amplitude from '@amplitude/unified';
import { config } from './env';

/**
 * Amplitude: product analytics and session replay, the same per-environment
 * project every Slate client reports to. Initialized exactly once, from
 * main.tsx, before the first render; events tracked before the SDK finishes
 * initializing are queued.
 *
 * The event names and property names live in slate-api/ANALYTICS.md and are
 * copied from there, not paraphrased, so a dashboard can count an admin action
 * beside the same action from any other client.
 */

/** Which Slate client this is. Attached to every event; call sites never repeat it. */
const PLATFORM = 'admin';

/** Event properties are short scalars, per the vocabulary. */
export type EventProps = Record<string, string | number | boolean | null | undefined>;

let started = false;

/**
 * Stamps `platform` on everything the SDK sends, including calls that reach
 * the namespace directly rather than going through `track` below. Properties
 * already on the event win, so nothing here can overwrite a call site.
 */
function addPlatformDefault(): void {
  void amplitude.add({
    name: 'slate-admin-platform',
    type: 'enrichment',
    setup: async () => undefined,
    execute: async (event) => ({
      ...event,
      event_properties: { platform: PLATFORM, ...event.event_properties },
    }),
  });
}

export function initAmplitude(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  const key = config.amplitudeApiKey;
  if (!key) {
    console.warn('Amplitude API key missing, analytics disabled');
    return;
  }
  amplitude.initAll(key, { analytics: { autocapture: false }, sessionReplay: { sampleRate: 1 } });
  addPlatformDefault();
}

/** One event. The name is a row in ANALYTICS.md, spelled exactly as it is there. */
export function track(name: string, props?: EventProps): void {
  amplitude.track(name, { platform: PLATFORM, ...props });
}

export interface Identity {
  /** The Slate admin id from GET /v1/admin/me, never the Firebase uid. */
  userId: string;
  email: string;
  name?: string | null;
}

/**
 * The gate re-mounts on every navigation, so the same admin would otherwise be
 * identified over and over. The last id sent is remembered until `reset`.
 */
let identified: string | null = null;

export function identify({ userId, email, name }: Identity): void {
  if (identified === userId) return;
  identified = userId;
  amplitude.setUserId(userId);
  const traits = new amplitude.Identify();
  traits.set('email', email);
  if (name) traits.set('name', name);
  void amplitude.identify(traits);
}

/** On sign out, so the next person at this browser is a new user. */
export function reset(): void {
  identified = null;
  amplitude.reset();
}

export { amplitude };
